<template>
  <div v-if="notifications.length > 0" :class="{
    'cube-notifications': true,
    ...props.extraClasses,
  }">
    <div class="cube-notifications__mark-all" v-if="notifications.length > 0">
      <h4 class="cube-notifications__title">
        {{ t('notifications.title') }}
      </h4>
      <a @click="markAllAsRead()" :class="{
        'cube-notifications__mark-all-link': true,
        'cube-notifications__mark-all-link--visible': visible,
      }">
        {{ t('notifications.markAllAsRead') }}
      </a>
    </div>
    <div :class="{
      'cube-notifications__content': true,
      'cube-notifications__content--visible': visible,
    }">
      <ul class="cube-notifications__list">
        <li
          v-for="notification in unreadNotifications"
          class="cube-notifications__list-item"
          :key="notification.id"
        >
          <a
            :class="{
              'cube-notifications__notification': true,
              'cube-notifications__notification--unread': notification.unread === 1,
            }"
            @click="markAsRead(notification)"
            :title="t('notifications.markAsRead')"
          >
            <span>
              {{ notification.body }}<br />
              {{ formatTimeAgo(notification.created_at) }}
            </span>
          </a>
        </li>
      </ul>
      <div
        v-if="notifications.length > maxNotifications"
        class="cube-notifications__more"
      >
        <a
          class="cube-notifications__more-link button button--secondary button--small"
          @click="maxNotifications += increaseBy"
        >
          {{ t('notifications.showMore') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { useStrings } from '@/composables/useStrings.ts'
  import { useDate } from '@/composables/useDate.ts'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { computed, ref } from 'vue'

  const { formatTimeAgo } = useDate()
  const cubeStore = useCubeStore()

  const props = defineProps({
    notifications: {
      type: Array,
        required: true,
    },
    cubeName: {
      type: String,
        required: true,
    },
    extraClasses: {
      type: Array,
    default: () => [],
    },
  })

  const { t } = useStrings()

  const maxNotifications = ref(2)
  const increaseBy = 4
  const visible = computed(() => unreadNotifications.value.length > 0)

  const unreadNotifications = computed(() => {
    const notifications = props.notifications.filter(
      (notification) => notification.unread === 1
    )

    if (notifications.length < maxNotifications.value) {
      return notifications
    }
    else {
      return notifications.slice(0, maxNotifications.value)
    }
  })

  const markAsRead = (notification) => {
    cubeStore.markNotificationsAsRead({ notification })
  }

  const markAllAsRead = () => {
    cubeStore.markNotificationsAsRead({ fromCubeName: props.cubeName })
  }
</script>

<style lang="scss">
  .cube-notifications {
    margin: $spacer10 0 $spacer40;
    width: 100%;

    &__title {
      margin: 0;
      font-size: $font-size-base;
      transition: $transition-base;
      display: inline-flex;
      align-items: center;
      color: $grey-dark;
    }

    &__has-unread {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 0.75em;
      height: 0.75em;
      background: $notification-color;
      padding: 0.25em;
      border-radius: 100%;
      color: $white;
      margin-right: $spacer05;
    }

    &__mark-all {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      overflow: hidden;

      &-link {
        transform: translateY(100%);
        transition: $transition-base, transform 0.3s ease-in-out 0s;

        &--visible {
          transform: translateY(0%);
          transition: $transition-base, transform 0.3s ease-in-out 0.3s;
        }
      }
    }

    &__content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-in-out 0.3s;

      &--visible {
        max-height: 100vh;
        transition: max-height 0.3s ease-in-out 0s;

        @media (min-height: $bp-xl) {
          max-height: 50vh;
        }
      }
    }

    &__list {
      list-style: none;
      margin: $spacer05 0 0;
      border-left: 4px solid $community;
      box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.1);
      padding: $spacer10 $spacer30 $spacer10 $spacer20;

      &-item {
        font-weight: bold;
        border-bottom: 1px solid $grey-lightest;

        &:last-child {
          border-bottom: none;
        }
      }
    }

    &__notification {
      cursor: pointer;
      transition: $transition-base;
      display: flex;
      justify-content: space-between;
      align-items: center;
      line-height: $line-height-sm;
      padding: $spacer10 0;
      font-weight: normal;

      &:hover,
      &:active,
      &:focus {
        color: $grey-dark;
      }

      &--unread {
        font-weight: bold;

        &::after {
          content: '';
          display: block;
          border-radius: 100%;
          width: $spacer075;
          height: $spacer075;
          background: $notification-color;
          flex-shrink: 0;
          margin-left: $spacer10;
        }
      }
    }

    &__more {
      margin-top: $spacer10;
      text-align: center;
    }
  }
</style>
